import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyTagsModule } from '@vendasta/galaxy/tags';
import {
  AvailableGuardrailType,
  AvailableGuardrailTypeFieldType,
  AvailableGuardrailTypeHandlingMode,
} from '@vendasta/protos/ai_assistants/v1alpha1';
import { catchError, combineLatest, map, merge, of, startWith, switchMap } from 'rxjs';
import {
  AiValidatorsFormArray,
  GuardrailFieldValue,
  GuardrailForm,
  isSupportedGuardrailType,
  offeredHandlingModes,
} from '../../../core/forms/ai-validators.form';
import { GuardrailTypesService } from '../../../core/services/guardrail-types.service';

export type GuardrailStage = 'input' | 'output';

export interface GuardrailRow {
  form: GuardrailForm;
  stage: GuardrailStage;
  spec?: AvailableGuardrailType;
}

export interface AddableGuardrailType {
  name: string;
  atCap: boolean;
}

type TypesState = { status: 'loading' } | { status: 'error' } | { status: 'ready'; types: AvailableGuardrailType[] };

const I18N = 'AI_ASSISTANT.SETTINGS.PROFILE_CAPABILITY.GUARDRAILS';
const LOADING: TypesState = { status: 'loading' };

@Component({
  selector: 'ai-guardrails-field',
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCheckboxModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    TranslateModule,
    GalaxyAlertModule,
    GalaxyFormFieldModule,
    GalaxyLoadingSpinnerModule,
    GalaxyTagsModule,
  ],
  templateUrl: './ai-guardrails-field.component.html',
  styleUrl: './ai-guardrails-field.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AiGuardrailsFieldComponent {
  private readonly guardrailTypes = inject(GuardrailTypesService);
  private readonly confirmationModal = inject(OpenConfirmationModalService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly inputValidators = input.required<AiValidatorsFormArray>();
  readonly outputValidators = input.required<AiValidatorsFormArray>();
  readonly disabled = input(false);

  protected readonly i18n = I18N;
  protected readonly FieldType = AvailableGuardrailTypeFieldType;
  protected readonly HandlingMode = AvailableGuardrailTypeHandlingMode;

  private readonly addSelect = viewChild<MatSelect>('addSelect');
  private readonly reload = signal(0);
  private readonly displayOrder = new WeakMap<GuardrailForm, number>();
  private nextDisplayOrder = 0;
  private disabledByUs = false;

  protected readonly typesState = toSignal(
    toObservable(this.reload).pipe(
      switchMap(() =>
        this.guardrailTypes.types$.pipe(
          map((types): TypesState => ({ status: 'ready', types })),
          startWith(LOADING),
          catchError(() => of<TypesState>({ status: 'error' })),
        ),
      ),
    ),
    { initialValue: LOADING },
  );

  private readonly specsByName = computed(() => {
    const state = this.typesState();
    const supported = state.status === 'ready' ? state.types.filter((type) => isSupportedGuardrailType(type.name)) : [];
    return new Map(supported.map((type) => [type.name, type]));
  });

  private readonly arraysChanged = toSignal(
    combineLatest([toObservable(this.inputValidators), toObservable(this.outputValidators)]).pipe(
      switchMap(([input, output]) => merge(input.valueChanges, output.valueChanges).pipe(startWith(null))),
    ),
  );

  readonly rows = computed<GuardrailRow[]>(() => {
    this.arraysChanged();
    const specs = this.specsByName();
    const tag = (array: AiValidatorsFormArray, stage: GuardrailStage): GuardrailRow[] =>
      array.controls.map((form) => ({
        form,
        stage,
        spec: form.passthrough ? undefined : specs.get(form.controls.name.value),
      }));
    const rows = [...tag(this.inputValidators(), 'input'), ...tag(this.outputValidators(), 'output')];
    for (const row of rows) {
      if (!this.displayOrder.has(row.form)) this.displayOrder.set(row.form, this.nextDisplayOrder++);
    }
    return rows.sort((a, b) => (this.displayOrder.get(a.form) ?? 0) - (this.displayOrder.get(b.form) ?? 0));
  });

  readonly addableTypes = computed<AddableGuardrailType[]>(() => {
    const counts = new Map<string, number>();
    for (const row of this.rows()) {
      const name = row.form.controls.name.value;
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }
    return [...this.specsByName().values()].map((type) => ({
      name: type.name,
      atCap: type.maxInstances > 0 && (counts.get(type.name) ?? 0) >= type.maxInstances,
    }));
  });

  constructor() {
    effect(() => {
      for (const row of this.rows()) {
        if (row.spec) row.form.applyTypeSpec(row.spec);
      }
    });

    effect(() => {
      const arrays = [this.inputValidators(), this.outputValidators()];
      if (this.disabled()) {
        arrays.forEach((array) => array.disable({ emitEvent: false }));
        this.disabledByUs = true;
      } else if (this.disabledByUs) {
        arrays.forEach((array) => array.enable({ emitEvent: false }));
        this.disabledByUs = false;
      }
    });
  }

  retry(): void {
    this.reload.update((n) => n + 1);
  }

  addGuardrail(name: string): void {
    const spec = this.specsByName().get(name);
    if (!spec || this.addableTypes().find((type) => type.name === name)?.atCap) return;
    this.outputValidators().push(GuardrailForm.forType(spec));
  }

  protected onAddSelected(select: MatSelect, name: string | null): void {
    if (name) this.addGuardrail(name);
    select.writeValue(null);
  }

  setStage(form: GuardrailForm, stage: GuardrailStage): void {
    const source = stage === 'input' ? this.outputValidators() : this.inputValidators();
    const target = stage === 'input' ? this.inputValidators() : this.outputValidators();
    const index = source.controls.indexOf(form);
    if (index < 0) return;
    source.removeAt(index);
    target.push(form);
  }

  removeGuardrail(form: GuardrailForm): void {
    this.confirmationModal
      .openModal({
        type: 'warn',
        title: `${I18N}.REMOVE_TITLE`,
        message: `${I18N}.REMOVE_MESSAGE`,
        confirmButtonText: `${I18N}.REMOVE`,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        for (const array of [this.inputValidators(), this.outputValidators()]) {
          const index = array.controls.indexOf(form);
          if (index >= 0) array.removeAt(index);
        }
        this.addSelect()?.focus();
      });
  }

  protected typeLabel(name: string): string {
    return this.label(`${I18N}.TYPES.${name}`, name);
  }

  protected fieldLabel(key: string): string {
    return this.label(`${I18N}.FIELDS.${key}`, key);
  }

  protected typeHint(name: string): string | null {
    return this.optional(`${I18N}.TYPE_HINTS.${name}`);
  }

  protected fieldHint(key: string): string | null {
    return this.optional(`${I18N}.FIELD_HINTS.${key}`);
  }

  protected fieldSuffixKey(key: string): string | undefined {
    const suffixKey = `${I18N}.FIELD_SUFFIXES.${key}`;
    return this.optional(suffixKey) ? suffixKey : undefined;
  }

  private optional(key: string): string | null {
    const translated = this.translate.instant(key);
    return translated === key ? null : translated;
  }

  /** Only a block drops the message; replace and redact still deliver it, rewritten. */
  protected stageHintKey(stage: GuardrailStage, handling: AvailableGuardrailTypeHandlingMode | null): string {
    const blocks = handling === AvailableGuardrailTypeHandlingMode.HANDLING_MODE_BLOCK ? '_BLOCK' : '';
    return `${I18N}.STAGE.${stage === 'input' ? 'INPUT' : 'OUTPUT'}${blocks}_HINT`;
  }

  protected offeredModes(spec: AvailableGuardrailType): AvailableGuardrailTypeHandlingMode[] {
    return offeredHandlingModes(spec);
  }

  /** One offered mode is not a choice; the block-message field says what happens. */
  protected showHandlingSelect(spec: AvailableGuardrailType): boolean {
    return offeredHandlingModes(spec).length > 1;
  }

  protected handlingLabelKey(mode: AvailableGuardrailTypeHandlingMode): string {
    return `${I18N}.HANDLING.${mode.replace('HANDLING_MODE_', '')}`;
  }

  protected runes(value: GuardrailFieldValue): number {
    return typeof value === 'string' ? Array.from(value).length : 0;
  }

  private label(key: string, fallback: string): string {
    const translated = this.translate.instant(key);
    return translated === key ? humanize(fallback) : translated;
  }
}

function humanize(key: string): string {
  const words = key.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}
